import { Button } from '@/src/components/atom/Button';
import Icon from '@/src/components/atom/Icon';
import Loader from '@/src/components/atom/Loader';
import Typo from '@/src/components/atom/Typo';
import SectionCard from '@/src/components/molecule/SectionCard';
import EditSvg from '@/src/assets/icons/icon-edit-pencel.svg';
import InfoSvg from '@/src/assets/icons/icon-info-gray6.svg';
import RedoSvg from '@/src/assets/icons/icon-redo-blue.svg';
import PhotoTypeEditDialog from '@/src/components/organism/PhotoTypeEditDialog';
import { useGetPhotoTypeConfigQuery } from '@/src/store/apis/photoType';
import { useCanEditPhotoType } from '@/src/hooks/useCanEditPhotoType';
import { useState, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FlexCenter } from './BasicInformation';
import colorSet from '@/src/styles/color';

/**
 * PhotoTypeLabelsCard Props
 * @note role prop은 CompanyManagement 하위 컴포넌트 API 일관성을 위해 유지
 *       (BasicInformation, BankDetails, SignatureManagement 등과 동일한 인터페이스)
 *       현재는 사용하지 않지만 향후 역할별 기능 확장 시 활용 예정
 */
interface PhotoTypeLabelsCardProps {
  /**
   * 사용자 역할 (향후 역할별 기능 확장 대비)
   * @see 현재 미사용 - CompanyManagement 하위 컴포넌트 API 일관성 유지 목적
   */
  role: 'EXPORTER' | 'IMPORTER';
}

/**
 * 고정 라벨 목록 (수정 불가)
 * @description Figma 디자인에 따라 Seal, Extra는 항상 고정으로 표시
 * @note 국제 해운/물류 업계 표준 용어로 영어 그대로 사용 (i18n 번역 불필요)
 */
const FIXED_LABELS = ['Close-Up Seal', 'Extra'] as const;

/**
 * PhotoTypeLabelsCard Component
 *
 * @description
 * CompanyManagement 페이지에서 로딩 사진 타입 라벨 설정을 표시하고 편집할 수 있는 카드 컴포넌트
 *
 * @features
 * - 활성화된 photo type labels 목록 표시
 * - Customizable Labels / Fixed Labels 섹션 구분
 * - Edit 버튼을 통한 PhotoTypeEditDialog 모달 열기
 * - 로딩 상태 처리
 * - SectionCard 스타일 기반 일관된 디자인
 *
 * @example
 * ```tsx
 * <PhotoTypeLabelsCard role="EXPORTER" />
 * ```
 */
const PhotoTypeLabelsCard: React.FC<PhotoTypeLabelsCardProps> = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  role: _role,
}) => {
  const { t } = useTranslation(['companyManagement', 'common']);
  const i18nPrefix = 'companyManagement:exporter.companyInfo.photoTypeLabels';
  const canEditPhotoType = useCanEditPhotoType();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const { data: configData, isLoading, isError, refetch } = useGetPhotoTypeConfigQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  // configData.photoTypeConfigs를 안정적으로 참조
  const photoTypeConfigs = configData?.photoTypeConfigs;

  /**
   * 활성화된 Customizable 라벨 목록
   * @description displayOrder 기준 정렬
   */
  const customizableLabels = useMemo(() => {
    if (!photoTypeConfigs) return [];
    return photoTypeConfigs
      .filter((config) => config.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [photoTypeConfigs]);

  const lastUpdatedAt = useMemo(() => {
    if (!photoTypeConfigs?.length) return null;
    return photoTypeConfigs
      .map((c) => c.updatedAt)
      .filter(Boolean)
      .sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf())[0] || null;
  }, [photoTypeConfigs]);

  const handleOpenEditDialog = useCallback(() => {
    setIsEditDialogOpen(true);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setIsEditDialogOpen(false);
  }, []);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <>
      <StyledSectionCard
        cardTitle={t(`${i18nPrefix}.title`)}
        rightAccessory={
          canEditPhotoType ? (
            <Button
              buttonGrade="tertiary"
              buttonColor="black"
              buttonSize={32}
              onClick={handleOpenEditDialog}
              disabled={isLoading || isError}
              isLoading={isLoading}
            >
              <FlexCenter>
                <Icon iconSrc={EditSvg} iconSize={16} />
                {t('common:edit')}
              </FlexCenter>
            </Button>
          ) : undefined
        }
      >
        {isLoading ? (
          <LoaderContainer role="status" aria-label={t('common:loading')}>
            <Loader />
          </LoaderContainer>
        ) : isError ? (
          <ErrorContainer role="alert">
            <Typo typoType="b7r" color="gray6">
              -
            </Typo>
            <RetryButton
              buttonGrade="tertiary"
              buttonColor="black"
              buttonSize={28}
              onClick={handleRetry}
              aria-label={t('common:reset')}
            >
              <Icon iconSrc={RedoSvg} iconSize={16} />
            </RetryButton>
          </ErrorContainer>
        ) : (
          <ContentContainer role="region" aria-label={t(`${i18nPrefix}.title`)}>
            {/* Customizable Labels Section */}
            <LabelSection>
              <LabelRow>
                <LabelCategory>
                  <Typo typoType="b6m" color="gray2">
                    {t(`${i18nPrefix}.customizableLabels`, {
                      count: customizableLabels.length,
                    })}
                  </Typo>
                </LabelCategory>
                <LabelValues>
                  {customizableLabels.length > 0 ? (
                    customizableLabels.map((label) => (
                      <LabelValue key={label.id}>
                        <Typo typoType="b6r" color="gray5">
                          {label.title}
                        </Typo>
                      </LabelValue>
                    ))
                  ) : (
                    <LabelValue>
                      <Typo typoType="b6r" color="gray7">
                        -
                      </Typo>
                    </LabelValue>
                  )}
                </LabelValues>
              </LabelRow>
            </LabelSection>

            {/* Divider */}
            <Divider />

            {/* Fixed Labels Section */}
            <LabelSection>
              <LabelRow>
                <LabelCategory>
                  <Typo typoType="b6m" color="gray2">
                    {t(`${i18nPrefix}.fixedLabels`, {
                      count: FIXED_LABELS.length,
                    })}
                  </Typo>
                </LabelCategory>
                <LabelValues>
                  {FIXED_LABELS.map((label) => (
                    <LabelValue key={label}>
                      <Typo typoType="b6r" color="gray5">
                        {label}
                      </Typo>
                    </LabelValue>
                  ))}
                </LabelValues>
              </LabelRow>
              <HintContainer>
                <Icon iconSrc={InfoSvg} iconSize={16} />
                <Typo typoType="b8r" color="gray6">
                  {t(`${i18nPrefix}.fixedLabelsHint`)}
                </Typo>
              </HintContainer>
            </LabelSection>

            {lastUpdatedAt && (
              <LastUpdatedRow>
                  <Dot />
                  <Typo typoType="b9r" color="gray4">
                    Last Updated
                  </Typo>
                  <Typo typoType="b9r" color="gray7">
                    {`(${dayjs(lastUpdatedAt).local().format('YY-MM-DD, HH:mm')})`}
                  </Typo>
                </LastUpdatedRow>
            )}
          </ContentContainer>
        )}
      </StyledSectionCard>

      <PhotoTypeEditDialog
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        onSuccess={handleCloseEditDialog}
      />
    </>
  );
};

export default PhotoTypeLabelsCard;

// ==================== Styled Components ====================

const StyledSectionCard = styled(SectionCard)`
  height: 100%;
`;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 200px;
`;

const RetryButton = styled(Button)`
  margin-top: 4px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const LabelSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const LabelCategory = styled.div`
  flex-shrink: 0;
  width: 164px;
  white-space: pre-wrap;
`;

const LabelValues = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const LabelValue = styled.div`
  line-height: 24px;
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${colorSet.gray9};
  margin: 16px 0;
`;

const LastUpdateDivider = styled.div`
  height: 1px;
  background-color: ${colorSet.gray9};
  margin-top: 16px;
`;

const HintContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 172px;
`;

const LastUpdatedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`;

const Dot = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: ${colorSet.gray7};
`;
